import React, { useEffect, useState, FormEvent } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/currency';
import { formatCompanyStatus } from '../utils/enumFormatting';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { API_URL } from '../constants/urls';

type Company = {
  id: string;
  name: string;
  segments?: Array<{ id: string; name: string }>;
  company_type?: string;
  annual_revenue?: number;
  employee_count?: number;
  overall_score?: number;
  revenue_tier?: string;
  notes?: string;
  status?: string;
  strategic_owner_id?: string;
  strategic_owner_name?: string;
  description?: string;
  website?: string;
  linkedin_url?: string;
};

type CompanyFormData = {
  name: string;
  segment_ids: string[];
  company_type: string;
  annual_revenue: number | string;
  employee_count: number | string;
  revenue_tier: string;
  notes: string;
  status: string;
  strategic_owner_id: string;
  description: string;
  website: string;
  linkedin_url: string;
};

const INITIAL_FORM_DATA: CompanyFormData = {
  name: '',
  segment_ids: [],
  company_type: '',
  annual_revenue: '',
  employee_count: '',
  revenue_tier: '',
  notes: '',
  status: 'suspect',
  strategic_owner_id: '',
  description: '',
  website: '',
  linkedin_url: '',
};

export default function CompaniesTailwind() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { selectedOrg: organization, userRole: userOrgRole } =
    useOrganization();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [segments, setSegments] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [orgMembers, setOrgMembers] = useState<
    Array<{ user_id: string; user_name: string }>
  >([]);
  const [open, setOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<CompanyFormData>(INITIAL_FORM_DATA);
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting state
  const [sortField, setSortField] = useState<keyof Company | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Filtering state
  const [filters, setFilters] = useState({
    status: '',
    company_type: '',
    segment: '',
    minEmployees: '',
    maxEmployees: '',
    minRevenue: '',
    maxRevenue: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const canEdit = userOrgRole === 'admin' || userOrgRole === 'editor';
  const canDelete = userOrgRole === 'admin' || userOrgRole === 'editor';

  const getAuthConfig = () => ({
    headers: { Authorization: `Bearer ${token}` },
  });

  useEffect(() => {
    if (!organization) return;
    fetchCompanies();
    fetchSegments();
    fetchOrgMembers();
  }, [organization]);

  const fetchCompanies = async () => {
    if (!organization || !token) return;
    try {
      const response = await axios.get(
        `${API_URL}/companies?organization_id=${organization.id}`,
        getAuthConfig(),
      );
      setCompanies(response.data);
    } catch (error) {
      // console.error removed
    }
  };

  const fetchSegments = async () => {
    if (!organization || !token) return;
    try {
      const response = await axios.get(
        `${API_URL}/segments?organization_id=${organization.id}`,
        getAuthConfig(),
      );
      setSegments(response.data);
    } catch (error) {
      // console.error removed
    }
  };

  const fetchOrgMembers = async () => {
    if (!organization || !token) return;
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organization.id}/members`,
        getAuthConfig(),
      );
      setOrgMembers(response.data);
    } catch (error) {
      // console.error removed
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditingCompany(null);
    setFormData(INITIAL_FORM_DATA);
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name || '',
      segment_ids: company.segments?.map((s) => s.id) || [],
      company_type: company.company_type || '',
      annual_revenue: company.annual_revenue || '',
      employee_count: company.employee_count || '',
      revenue_tier: company.revenue_tier || '',
      notes: company.notes || '',
      status: company.status || 'suspect',
      strategic_owner_id: company.strategic_owner_id || '',
      description: company.description || '',
      website: company.website || '',
      linkedin_url: company.linkedin_url || '',
    });
    setOpen(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!organization || !token) return;

    const submitData = {
      ...formData,
      annual_revenue:
        formData.annual_revenue === '' ? null : Number(formData.annual_revenue),
      employee_count:
        formData.employee_count === '' ? null : Number(formData.employee_count),
      strategic_owner_id:
        formData.strategic_owner_id === '' ? null : formData.strategic_owner_id,
    };

    try {
      if (editingCompany) {
        await axios.put(
          `${API_URL}/companies/${editingCompany.id}?organization_id=${organization.id}`,
          submitData,
          getAuthConfig(),
        );
      } else {
        await axios.post(
          `${API_URL}/companies?organization_id=${organization.id}`,
          submitData,
          getAuthConfig(),
        );
      }
      fetchCompanies();
      handleClose();
    } catch (error) {
      // console.error removed
      alert('Failed to save company. Please try again.');
    }
  };

  const handleDelete = async (companyId: string) => {
    if (
      !organization ||
      !token ||
      !window.confirm('Are you sure you want to delete this company?')
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/companies/${companyId}?organization_id=${organization.id}`,
        getAuthConfig(),
      );
      fetchCompanies();
    } catch (error) {
      // console.error removed
    }
  };

  // Get unique values for filter dropdowns
  const uniqueStatuses = Array.from(
    new Set(companies.map((c) => c.status || 'target')),
  );
  const uniqueCompanyTypes = Array.from(
    new Set(companies.map((c) => c.company_type).filter(Boolean)),
  );
  const uniqueSegments = Array.from(
    new Set(companies.flatMap((c) => c.segments?.map((s) => s.name) || [])),
  );

  // Sorting function
  const handleSort = (field: keyof Company) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort companies
  const filteredCompanies = companies
    .filter((company) => {
      // Text search
      const matchesSearch = company.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus =
        !filters.status || (company.status || 'target') === filters.status;

      // Company type filter
      const matchesType =
        !filters.company_type || company.company_type === filters.company_type;

      // Segment filter
      const matchesSegment =
        !filters.segment ||
        company.segments?.some((s) => s.name === filters.segment);

      // Employee count filter
      const matchesMinEmployees =
        !filters.minEmployees ||
        (company.employee_count &&
          company.employee_count >= parseInt(filters.minEmployees));
      const matchesMaxEmployees =
        !filters.maxEmployees ||
        (company.employee_count &&
          company.employee_count <= parseInt(filters.maxEmployees));

      // Revenue filter
      const matchesMinRevenue =
        !filters.minRevenue ||
        (company.annual_revenue &&
          company.annual_revenue >= parseInt(filters.minRevenue));
      const matchesMaxRevenue =
        !filters.maxRevenue ||
        (company.annual_revenue &&
          company.annual_revenue <= parseInt(filters.maxRevenue));

      return (
        matchesSearch &&
        matchesStatus &&
        matchesType &&
        matchesSegment &&
        matchesMinEmployees &&
        matchesMaxEmployees &&
        matchesMinRevenue &&
        matchesMaxRevenue
      );
    })
    .sort((a, b) => {
      if (!sortField) return a.name.localeCompare(b.name);

      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle special cases
      if (sortField === 'segments') {
        aValue = a.segments?.map((s) => s.name).join(', ') || '';
        bValue = b.segments?.map((s) => s.name).join(', ') || '';
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

      // Sort by type
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const result = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? result : -result;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const result = aValue - bValue;
        return sortDirection === 'asc' ? result : -result;
      }

      return 0;
    });

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: '',
      company_type: '',
      segment: '',
      minEmployees: '',
      maxEmployees: '',
      minRevenue: '',
      maxRevenue: '',
    });
  };

  // Sort icon component
  const SortIcon = ({ field }: { field: keyof Company }) => {
    if (sortField !== field) {
      return <div className="w-4 h-4" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="w-4 h-4" />
    ) : (
      <ChevronDownIcon className="w-4 h-4" />
    );
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'suspect':
        return 'bg-purple-100 text-purple-700';
      case 'target':
        return 'bg-gray-100 text-gray-700';
      case 'prospect':
        return 'bg-blue-100 text-blue-700';
      case 'customer':
        return 'bg-green-100 text-green-700';
      case 'former_customer':
        return 'bg-yellow-100 text-yellow-700';
      case 'lost':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (!organization) {
    return (
      <div className="p-6">
        <p className="text-gray-600">
          Please select an organization to view companies.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold">Companies</h1>
        {canEdit && (
          <button
            onClick={handleOpen}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Company</span>
          </button>
        )}
      </div>

      {/* Search and Filter Controls */}
      <div className="mb-6 space-y-4">
        {/* Search bar and filter toggle */}
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-md transition-colors ${
              showFilters
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'border-gray-300 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            <span>Filters</span>
            {Object.values(filters).some((f) => f !== '') && (
              <span className="bg-blue-600 text-white text-xs rounded-full px-2 py-1 ml-1">
                {Object.values(filters).filter((f) => f !== '').length}
              </span>
            )}
          </button>
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="bg-gray-50 p-4 rounded-lg border">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All statuses</option>
                  {uniqueStatuses.map((status) => (
                    <option key={status} value={status}>
                      {formatCompanyStatus(status)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Company type filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Type
                </label>
                <select
                  value={filters.company_type}
                  onChange={(e) =>
                    setFilters({ ...filters, company_type: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All types</option>
                  {uniqueCompanyTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Segment filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Segment
                </label>
                <select
                  value={filters.segment}
                  onChange={(e) =>
                    setFilters({ ...filters, segment: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All segments</option>
                  {uniqueSegments.map((segment) => (
                    <option key={segment} value={segment}>
                      {segment}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear filters button */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={Object.values(filters).every((f) => f === '')}
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Employee and revenue range filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee Count Range
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minEmployees}
                    onChange={(e) =>
                      setFilters({ ...filters, minEmployees: e.target.value })
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="self-center text-gray-500">to</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxEmployees}
                    onChange={(e) =>
                      setFilters({ ...filters, maxEmployees: e.target.value })
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Annual Revenue Range
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minRevenue}
                    onChange={(e) =>
                      setFilters({ ...filters, minRevenue: e.target.value })
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="self-center text-gray-500">to</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxRevenue}
                    onChange={(e) =>
                      setFilters({ ...filters, maxRevenue: e.target.value })
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Companies Table/Cards */}
      <div className="hidden lg:block overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  <span>Company</span>
                  <SortIcon field="name" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-2">
                  <span>Status</span>
                  <SortIcon field="status" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('segments')}
              >
                <div className="flex items-center gap-2">
                  <span>Segments</span>
                  <SortIcon field="segments" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('company_type')}
              >
                <div className="flex items-center gap-2">
                  <span>Type</span>
                  <SortIcon field="company_type" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('strategic_owner_name')}
              >
                <div className="flex items-center gap-2">
                  <span>Owner</span>
                  <SortIcon field="strategic_owner_name" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('employee_count')}
              >
                <div className="flex items-center gap-2">
                  <span>Employees</span>
                  <SortIcon field="employee_count" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('annual_revenue')}
              >
                <div className="flex items-center gap-2">
                  <span>Annual Revenue</span>
                  <SortIcon field="annual_revenue" />
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCompanies.map((company) => (
              <tr key={company.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <button
                      onClick={() => navigate(`/companies/${company.id}`)}
                      className="text-sm font-medium text-gray-900 hover:text-blue-600 text-left"
                    >
                      {company.name}
                    </button>
                    <div className="flex gap-3 mt-1">
                      {company.website && (
                        <a
                          href={
                            company.website.startsWith('http')
                              ? company.website
                              : `https://${company.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Website
                        </a>
                      )}
                      {company.linkedin_url && (
                        <a
                          href={
                            company.linkedin_url.startsWith('http')
                              ? company.linkedin_url
                              : `https://${company.linkedin_url}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${getStatusColor(company.status)}`}
                  >
                    {formatCompanyStatus(company.status)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {company.segments?.map((segment) => (
                      <span
                        key={segment.id}
                        className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs"
                      >
                        {segment.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {company.company_type || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {company.strategic_owner_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {company.employee_count?.toLocaleString() || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {company.annual_revenue
                    ? formatCurrency(
                        company.annual_revenue,
                        organization.currency || 'USD',
                        {
                          compact: true,
                        },
                      )
                    : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => navigate(`/companies/${company.id}`)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => handleEdit(company)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(company.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile and tablet view - Cards */}
      <div className="lg:hidden space-y-4">
        {filteredCompanies.map((company) => (
          <div key={company.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <button
                  onClick={() => navigate(`/companies/${company.id}`)}
                  className="font-semibold text-lg text-gray-900 hover:text-blue-600 text-left"
                >
                  {company.name}
                </button>
                <div className="flex gap-2">
                  {company.website && (
                    <a
                      href={
                        company.website.startsWith('http')
                          ? company.website
                          : `https://${company.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Website
                    </a>
                  )}
                  {company.linkedin_url && (
                    <a
                      href={
                        company.linkedin_url.startsWith('http')
                          ? company.linkedin_url
                          : `https://${company.linkedin_url}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>
              <span
                className={`px-2 py-1 rounded-full text-xs ${getStatusColor(company.status)}`}
              >
                {formatCompanyStatus(company.status)}
              </span>
            </div>

            {company.segments && company.segments.length > 0 && (
              <div className="mb-2">
                <div className="flex flex-wrap gap-1">
                  {company.segments.map((segment) => (
                    <span
                      key={segment.id}
                      className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs"
                    >
                      {segment.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="text-sm text-gray-600 space-y-1">
              {company.company_type && <p>Type: {company.company_type}</p>}
              {company.strategic_owner_name && (
                <p>Owner: {company.strategic_owner_name}</p>
              )}
              {company.employee_count && (
                <p>Employees: {company.employee_count.toLocaleString()}</p>
              )}
              {company.annual_revenue && (
                <p>
                  Revenue:{' '}
                  {formatCurrency(
                    company.annual_revenue,
                    organization.currency || 'USD',
                    {
                      compact: true,
                    },
                  )}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
              <button
                onClick={() => navigate(`/companies/${company.id}`)}
                className="text-blue-600 hover:text-blue-900"
                title="View"
              >
                <EyeIcon className="h-5 w-5" />
              </button>
              {canEdit && (
                <button
                  onClick={() => handleEdit(company)}
                  className="text-yellow-600 hover:text-yellow-900"
                  title="Edit"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => handleDelete(company.id)}
                  className="text-red-600 hover:text-red-900"
                  title="Delete"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      {open && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={handleClose}
            ></div>

            <div className="relative bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold mb-4">
                {editingCompany ? 'Edit Company' : 'Add New Company'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="suspect">Suspect</option>
                      <option value="target">Target</option>
                      <option value="prospect">Prospect</option>
                      <option value="customer">Customer</option>
                      <option value="former_customer">Former Customer</option>
                      <option value="lost">Lost</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Type
                    </label>
                    <input
                      type="text"
                      value={formData.company_type}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          company_type: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Strategic Owner
                    </label>
                    <select
                      value={formData.strategic_owner_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          strategic_owner_id: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Unassigned</option>
                      {orgMembers.map((member) => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.user_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) =>
                        setFormData({ ...formData, website: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LinkedIn URL
                    </label>
                    <input
                      type="url"
                      value={formData.linkedin_url}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          linkedin_url: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employee Count
                    </label>
                    <input
                      type="number"
                      value={formData.employee_count}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          employee_count: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Annual Revenue
                    </label>
                    <input
                      type="number"
                      value={formData.annual_revenue}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          annual_revenue: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Segments
                    </label>
                    <div className="space-y-2">
                      {segments.map((segment) => (
                        <label key={segment.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.segment_ids.includes(segment.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  segment_ids: [
                                    ...formData.segment_ids,
                                    segment.id,
                                  ],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  segment_ids: formData.segment_ids.filter(
                                    (id) => id !== segment.id,
                                  ),
                                });
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm">{segment.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingCompany ? 'Save Changes' : 'Add Company'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Results count and empty state */}
      {companies.length > 0 && (
        <div className="text-sm text-gray-600 mb-4">
          Showing {filteredCompanies.length} of {companies.length} companies
          {Object.values(filters).some((f) => f !== '') || searchTerm
            ? ' (filtered)'
            : ''}
        </div>
      )}

      {/* Empty state */}
      {filteredCompanies.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {companies.length === 0
              ? 'No companies yet. Add your first company to get started.'
              : searchTerm || Object.values(filters).some((f) => f !== '')
                ? 'No companies found matching your search and filters.'
                : 'No companies to display.'}
          </p>
          {(searchTerm || Object.values(filters).some((f) => f !== '')) &&
            companies.length > 0 && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  clearFilters();
                }}
                className="mt-2 text-blue-600 hover:text-blue-800 underline"
              >
                Clear search and filters
              </button>
            )}
        </div>
      )}
    </div>
  );
}
